import { toast } from '@design-system/index'
import { s3URL } from '@mathflat/common/util'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { action, makeObservable } from 'mobx'

import { examController } from '@/controller/exam.controller'
import { examGroupScoringController } from '@/controller/examGroupScoring.controller'
import type { ExamSchedule } from '@/dto/Exam/ExamSchedule'
import {
  BaseExamService,
  type ExamGrade,
  type ExamSchoolType,
} from '@/page/Lesson-prepare/_common/Exam/services/BaseExamService'
import { curriculumCoreService } from '@/service/curriculumCore.service'
import { downloadFileByUrl } from '@/utils/download'
import { commonErrorMessage, toastSuccess } from '@/utils/toast'

export class KMMRegisterService extends BaseExamService {
  constructor() {
    super()
    makeObservable(this, {
      initialize: action.bound,
      fetchExamByYearMonth: action.bound,
      assignExamToStudent: action.bound,
      unassignExamFromStudent: action.bound,
    })
  }

  getKmmCurriculumsForThemeWorksheet = () => {
    const includeHighSchool = this.selectedExamSchedule?.hasHighExam ?? false
    const filteredCurriculums = includeHighSchool
      ? curriculumCoreService.getKmmCurriculumForThemeWorksheet()
      : curriculumCoreService
          .getKmmCurriculumForThemeWorksheet()
          .filter((item) => item.schoolType !== 'HIGH')

    return filteredCurriculums.map((item) => ({
      ...item,
      labelShort: item.isAllType ? '전체' : item.labelShort,
      subscribeStatus: item.subscribeStatus,
    }))
  }

  getKmmCurriculumsAutoAssigning = () =>
    curriculumCoreService.getKmmAutoAssigningGrade().map((item) => ({
      ...item,
      subscribeStatus: item.subscribeStatus,
    }))

  // 추상 메서드 구현: 초기화
  async initialize() {
    try {
      const [kmmSchedules, practiceSchedules] = await Promise.all([
        examController.getExamSchedule('KMM'),
        examController.getExamSchedule('KMM_PRACTICE_TEST'),
      ])
      this._examSchedule = mergeExamSchedules(kmmSchedules, practiceSchedules)

      const currentExamSchedule = this._examSchedule.find(({ isCurrentExam }) => isCurrentExam)
      if (currentExamSchedule) {
        await this.selectYearMonth(currentExamSchedule.yearMonth)
      }
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  // 추상 메서드 구현: 시험 데이터 fetch
  async fetchExamByYearMonth(yearMonth: ExamSchedule['yearMonth']) {
    const exam = await examController.getExamByYearMonth({ yearMonth })
    this._selectedExams = exam
    return exam
  }

  // 추상 메서드 구현: 리포트 다운로드
  async downloadReports(examId: res.AssignedExam['id']) {
    const { presignedUrl, fileName } = await examController.getReport({
      type: 'EXAM',
      examId,
    })
    downloadFileByUrl(presignedUrl, { fullFileName: fileName })
  }

  async downloadStudentReport(
    examId: res.AssignedExam['id'],
    studentId: res.AssignedStudent['studentId'],
  ) {
    const { presignedUrl, fileName: filename } = await examController.getReport({
      type: 'STUDENT_EXAM',
      examId,
      studentId,
    })
    downloadFileByUrl(presignedUrl, { fullFileName: filename })
  }

  // KMM 전용: 채점
  async scoreStudentExams(
    examId: res.AssignedExam['id'],
    studentExamIds: res.AssignedStudent['id'][],
  ) {
    const scoringSummary = await examGroupScoringController.fetchScoringSummary(
      examId,
      studentExamIds,
    )
    return scoringSummary
  }

  // 추상 메서드 구현: 학생 출제/취소
  async assignExamToStudent({
    schoolType,
    grade,
    semester,
    studentIdList,
  }: {
    schoolType: ExamSchoolType
    grade: ExamGrade
    semester?: common.Semester
    studentIdList: res.AssignedStudent['studentId'][]
  }) {
    try {
      const targetExam = this.getExamBySchoolGrade(schoolType, grade, semester)
      if (!targetExam) return
      await examController.assignStudentExam(targetExam.id, studentIdList)
      if (this.selectedYearMonth) {
        await this.fetchExamByYearMonth(this.selectedYearMonth)
      }
      toastSuccess(`시험이 ${tJosa('출제')}되었습니다.`)
    } catch (e) {
      if (e.code === 'STUDENT_EXAM_ALREADY_ASSIGNED_IN_KMM_PRACTICE_TEST') {
        toast.failure(
          `고등부 시험은 중복 ${tJosa('출제')}할 수 없어요. ${tJosa('출제')}이력을 확인해주세요.`,
        )
        return
      }
      commonErrorMessage(e)
    }
  }

  async unassignExamFromStudent(
    studentDataRelationIds: res.AssignedStudent['studentWorksheetId'][],
  ) {
    try {
      await examController.unAssignStudentExam(studentDataRelationIds)
      if (this.selectedYearMonth) {
        await this.fetchExamByYearMonth(this.selectedYearMonth)
      }
      toastSuccess(`${tJosa('출제', '이/가')} 취소되었습니다.`)
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  // 추상 메서드 구현: PDF 다운로드
  downloadExamScopeAsPdf() {
    const downloadPath = s3URL.freewheelinContentsBucket('kmm/KMM_exam_scope_20250819.pdf')
    downloadFileByUrl(downloadPath, { fullFileName: 'KMM_수학경시대회_시험범위.pdf' })
  }
}

function mergeExamSchedules(
  kmmSchedules: ExamSchedule[],
  practiceSchedules: ExamSchedule[],
): ExamSchedule[] {
  const practiceMap = new Map(practiceSchedules.map((s) => [s.yearMonth, s]))

  const kmmMerged = kmmSchedules.map((kmm) => {
    const practice = practiceMap.get(kmm.yearMonth)
    if (practice) {
      practiceMap.delete(kmm.yearMonth)
      // spread는 prototype getter를 잃으므로 Object.create로 prototype 보존
      const merged = Object.create(Object.getPrototypeOf(kmm)) as ExamSchedule
      Object.assign(merged, kmm, {
        highSchoolProgressTimeline: {
          examStartDatetime: practice.progressTimeline.examStartDatetime,
          examEndDatetime: practice.progressTimeline.examEndDatetime,
          gradingStartDatetime: practice.progressTimeline.gradingStartDatetime,
          gradingEndDatetime: practice.progressTimeline.gradingEndDatetime,
          awardRequestStartDatetime: practice.progressTimeline.awardRequestStartDatetime,
          awardRequestEndDatetime: practice.progressTimeline.awardRequestEndDatetime,
        },
        exams: [...kmm.exams, ...practice.exams],
      })
      return merged
    }
    return kmm
  })

  // KMM 없이 PRACTICE_TEST만 있는 달 추가
  return [...kmmMerged, ...practiceMap.values()]
}

const kmmRegisterService = new KMMRegisterService()
export default kmmRegisterService
